import React from 'react';
import { motion } from 'framer-motion';
import { FaBrain, FaRobot, FaServer, FaCogs } from 'react-icons/fa';
import './About.css';

const highlights = [
  {
    index: '01',
    icon: <FaRobot />,
    label: 'AI Agents & Orchestration',
    desc: 'LangGraph, LangChain & Model Context Protocol (MCP) tool integration for autonomous multi-turn systems.',
  },
  {
    index: '02',
    icon: <FaBrain />,
    label: 'Advanced RAG & LLM Fine-tuning',
    desc: 'Structure-aware chunking, hybrid search, PostgreSQL graph search, RRF & Qwen3-8B fine-tuning with Ragas.',
  },
  {
    index: '03',
    icon: <FaCogs />,
    label: 'Deep Learning & Vision',
    desc: 'Swin Transformers, ViT benchmarks, Grad-CAM interpretability & PyTorch production training.',
  },
  {
    index: '04',
    icon: <FaServer />,
    label: 'Production MLOps & Deployment',
    desc: 'Docker Compose, MLflow registry, Apache Airflow DAGs, FastAPI serving & Evidently AI drift monitoring.',
  },
];

const softSkills = [
  'Problem-solving',
  'Time Management',
  'Communication',
  'Adaptable',
  'Team Player',
  'Presentation',
];

const About = () => {
  return (
    <section id="about" className="about">
      <div className="container">
        <div className="section-header">
          <span className="section-label">[ 01 // OVERVIEW ]</span>
          <h2 className="section-title">Professional Summary</h2>
          <p className="section-subtitle">
            Engineering robust, production-grade intelligence from research and modeling to deployment.
          </p>
        </div>

        <motion.div
          className="about__content"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="about__text">
            <p>
              I am an <strong>AI Engineer</strong> with experience in building intelligent AI agents,
              retrieval-augmented generation (RAG) based chatbots, and deep learning models. Skilled
              in designing scalable AI solutions, developing APIs for models, and deploying them
              using Docker.
            </p>
            <p>
              I am eager to contribute to innovative projects focused on <strong>AI agents</strong>,{' '}
              <strong>conversational intelligence</strong>, and <strong>end-to-end AI deployment</strong>.
              My workflow bridges cutting-edge research with production engineering — from fine-tuning open-weights
              models to building automated drift-monitoring MLOps pipelines.
            </p>

            <div className="about__soft-skills">
              <span className="about__soft-label">[ SOFT SKILLS ]</span>
              <div className="about__pills">
                {softSkills.map((skill) => (
                  <span className="tag" key={skill}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="about__highlights">
            {highlights.map((item, i) => (
              <motion.div
                className="about__card"
                key={item.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
              >
                <div className="about__card-header">
                  <span className="about__card-index">{item.index}</span>
                  <div className="about__card-icon">{item.icon}</div>
                </div>
                <div className="about__card-info">
                  <h4>{item.label}</h4>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
